import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Form, FormGroup, FormLabel, InputGroup } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

// Constants
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
const PREVIEW_IMAGE_SIZE = 60;

// File icon component
const FileIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
            stroke="#6C757D"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M14 2V8H20"
            stroke="#6C757D"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const FileUpload = ({
    label,
    name,
    rules,
    fileName = null,
    placeholder = "Choose a file...",
    readOnly = false,
    accept = "*",
    containerProps = {},
    uploadProps = {},
}) => {
    const { register, formState: { errors }, setValue, watch, getValues } = useFormContext();
    const [previewUrl, setPreviewUrl] = useState(null);
    const [fileState, setFileState] = useState(null);
    const [isImage, setIsImage] = useState(false);

    register(name, rules);
    const uploadedFile = watch(name);

    // Helper function to check if file is an image
    const checkIfImage = useCallback((file) => {
        if (file instanceof File) {
            return file.type.startsWith('image/');
        }

        if (typeof file === 'string') {
            return IMAGE_EXTENSIONS.some(ext => file.toLowerCase().includes(ext));
        }

        if (file && typeof file === 'object') {
            if (file.mimeType) {
                return file.mimeType.startsWith('image/');
            }
            if (file.path) {
                return IMAGE_EXTENSIONS.some(ext => file.path.toLowerCase().includes(ext));
            }
        }

        return false;
    }, []);

    // Helper function to get filename from various file types
    const getFileName = useCallback((file, attachmentData = null) => {
        if (fileName) return fileName;
        if (attachmentData?.originalName) return attachmentData.originalName;
        if (attachmentData?.name) return attachmentData.name;
        if (file?.name) return file.name;
        if (typeof file === 'string') return file.split("/").pop();
        return "Uploaded file";
    }, [fileName]);

    // Helper function to get display filename
    const getDisplayFileName = useCallback(() => {
        if (fileState?.name) return fileState.name;
        if (uploadedFile && typeof uploadedFile === 'object') {
            return uploadedFile.originalName || uploadedFile.name;
        }
        return "Uploaded file";
    }, [fileState, uploadedFile]);

    // Fetch file from URL and convert to File object
    const fetchFileFromUrl = useCallback(async (url, attachmentData = null) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status}`);
            }

            const blob = await response.blob();
            const finalFileName = getFileName(null, attachmentData) || url.split("/").pop();
            const file = new File([blob], finalFileName, { type: blob.type });

            setFileState(file);
            setValue(name, file);
            setIsImage(checkIfImage(attachmentData || file));
        } catch (error) {
            console.error("Error converting URL to File:", error);
            setFileState(null);
            setIsImage(false);
        }
    }, [setValue, name, getFileName, checkIfImage]);

    // Reset file state
    const resetFileState = useCallback(() => {
        setPreviewUrl(null);
        setFileState(null);
        setIsImage(false);
    }, []);

    // Handle file upload changes
    useEffect(() => {
        if (!uploadedFile) {
            resetFileState();
            return;
        }

        // Handle attachment object with path
        if (uploadedFile && typeof uploadedFile === 'object' && uploadedFile.path) {
            const fullUrl = process.env.REACT_APP_API_URL + uploadedFile.path;
            setPreviewUrl(fullUrl);
            setIsImage(checkIfImage(uploadedFile));
            fetchFileFromUrl(fullUrl, uploadedFile);
            return;
        }

        // Handle simple string path (backward compatibility)
        if (typeof uploadedFile === "string") {
            const fullUrl = process.env.REACT_APP_API_URL + uploadedFile;
            setPreviewUrl(fullUrl);
            setIsImage(checkIfImage(uploadedFile));
            fetchFileFromUrl(fullUrl);
            return;
        }

        // Handle File object
        if (uploadedFile instanceof File) {
            const url = URL.createObjectURL(uploadedFile);
            setPreviewUrl(url);
            setFileState(uploadedFile);
            setIsImage(checkIfImage(uploadedFile));

            // Cleanup function to revoke object URL
            return () => URL.revokeObjectURL(url);
        }
    }, [uploadedFile, fetchFileFromUrl, checkIfImage, resetFileState]);

    // Handle file input change
    const handleFileChange = useCallback((event) => {
        const file = event.target.files?.[0];
        if (file) {
            setFileState(file);
            setValue(name, file);
        }
    }, [setValue, name]);

    // Remove file handler
    const handleRemoveFile = useCallback(() => {
        resetFileState();
        setValue(name, null);

        // Clear file input
        const fileInput = document.getElementById(name);
        if (fileInput) {
            fileInput.value = '';
        }
    }, [resetFileState, setValue, name]);

    // Memoized styles
    const containerStyle = useMemo(() => ({
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        border: "1px solid #ddd",
        padding: "10px",
        borderRadius: "4px",
    }), []);

    const previewContainerStyle = useMemo(() => ({
        gap: "10px",
        marginTop: "10px",
    }), []);

    const previewContentStyle = useMemo(() => ({
        gap: "10px",
        flex: 1
    }), []);

    const imageStyle = useMemo(() => ({
        width: `${PREVIEW_IMAGE_SIZE}px`,
        height: `${PREVIEW_IMAGE_SIZE}px`,
        objectFit: "cover",
        borderRadius: "4px",
        border: "1px solid #ddd"
    }), []);

    const linkStyle = useMemo(() => ({
        textDecoration: "none",
        color: "black",
        cursor: "pointer",
        fontSize: "0.9em",
        wordBreak: "break-word"
    }), []);

    const removeButtonStyle = useMemo(() => ({
        backgroundColor: "transparent",
        border: "none",
        color: "red",
        cursor: "pointer",
        fontWeight: "bold",
        fontSize: "0.9em",
    }), []);

    return (
        <FormGroup className="mb-3" {...containerProps}>
            {label && (
                <FormLabel htmlFor={name}>
                    {label}
                    {rules?.required && <span className="text-danger ms-1">*</span>}
                </FormLabel>
            )}

            <InputGroup hasValidation className="w-100">
                <div className="file-upload-box" style={{ ...containerStyle, width: "100%" }}>
                    {!readOnly && (
                        <Form.Control
                            type="file"
                            id={name}
                            onChange={handleFileChange}
                            isInvalid={!!errors[name] && !getValues()?.[name]}
                            accept={accept}
                            {...uploadProps}
                        />
                    )}

                    {previewUrl && (
                        <div
                            className="d-flex align-items-center justify-content-between"
                            style={previewContainerStyle}
                        >
                            <div className="d-flex align-items-center" style={previewContentStyle}>
                                {isImage ? (
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        style={imageStyle}
                                    />
                                ) : (
                                    <FileIcon />
                                )}

                                <a
                                    href={previewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={linkStyle}
                                >
                                    {getDisplayFileName()}
                                </a>
                            </div>

                            {!readOnly && (
                                <button
                                    type="button"
                                    onClick={handleRemoveFile}
                                    style={removeButtonStyle}
                                    aria-label="Remove file"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    )}

                    <Form.Control.Feedback type="invalid">
                        {errors[name]?.message}
                    </Form.Control.Feedback>
                </div>
            </InputGroup>
        </FormGroup>
    );
};

export default FileUpload;
